import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { Calculator, Loader2, AlertTriangle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RiskCalculator() {
    const [markets, setMarkets] = useState([]);
    const [form, setForm] = useState({ market: '', entry_price: '', stop_loss: '', lot_size: '' });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        axios.get(`${API}/tools/risk-calc/markets`, { withCredentials: true })
            .then((res) => setMarkets(res.data.markets))
            .catch(() => {});
    }, []);

    const canSubmit = form.market && form.entry_price && form.stop_loss && form.lot_size;

    const calculate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setResult(null);
        try {
            const res = await axios.post(`${API}/tools/risk-calc`, {
                market: form.market,
                entry_price: parseFloat(form.entry_price),
                stop_loss: parseFloat(form.stop_loss),
                lot_size: parseFloat(form.lot_size),
            }, { withCredentials: true });
            setResult(res.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Calculation failed');
        } finally {
            setLoading(false);
        }
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base, #07090B)' }}>
            <Header />
            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="risk-calculator-page">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                        <Calculator className="w-7 h-7" style={{ color: 'var(--primary-color, #00FFA3)' }} />
                        Risk Calculator
                    </h1>
                    <p className="text-sm mt-1.5" style={{ color: 'var(--text-secondary)' }}>
                        Know approximately how many dollars are at risk before you take a trade.
                    </p>
                </div>

                <form
                    onSubmit={calculate}
                    className="rounded-lg p-6 space-y-5"
                    style={{ backgroundColor: 'var(--background-elevated, #101418)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.08))' }}
                >
                    <div className="space-y-1.5">
                        <Label>Market</Label>
                        <Select value={form.market} onValueChange={(v) => setForm((f) => ({ ...f, market: v }))}>
                            <SelectTrigger data-testid="risk-market-select">
                                <SelectValue placeholder="Select a market (e.g. BTCUSD, GBPAUD)" />
                            </SelectTrigger>
                            <SelectContent>
                                {markets.map((m) => (
                                    <SelectItem key={m} value={m}>{m}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Entry Price</Label>
                            <Input type="number" step="any" min="0" placeholder="e.g. 4120.00" value={form.entry_price} onChange={set('entry_price')} data-testid="risk-entry-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Stop Loss Price</Label>
                            <Input type="number" step="any" min="0" placeholder="e.g. 4115.00" value={form.stop_loss} onChange={set('stop_loss')} data-testid="risk-sl-input" />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Lot Size (standard USD lots)</Label>
                        <Input type="number" step="any" min="0" placeholder="e.g. 0.01" value={form.lot_size} onChange={set('lot_size')} data-testid="risk-lot-input" />
                    </div>
                    <Button type="submit" className="w-full" disabled={!canSubmit || loading} data-testid="risk-calculate-btn">
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                        Calculate Risk
                    </Button>
                </form>

                {result && (
                    <div
                        className="rounded-lg p-6 space-y-4"
                        style={{ backgroundColor: 'rgba(0,255,163,0.05)', border: '1px solid rgba(0,255,163,0.25)' }}
                        data-testid="risk-result-card"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Approximate amount at risk</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-white/5 border border-white/10" style={{ color: 'var(--text-secondary)' }}>{result.market}</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <DollarSign className="w-8 h-8" style={{ color: 'var(--primary-color, #00FFA3)' }} />
                            <span className="text-4xl font-bold font-mono" style={{ color: 'var(--primary-color, #00FFA3)' }} data-testid="risk-usd-amount">
                                {result.risk_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>USD</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm pt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                            <div>
                                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Price distance</div>
                                <div className="font-mono">{result.price_distance}{result.pips ? ` (${result.pips} pips)` : ''}</div>
                            </div>
                            <div>
                                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Contract size</div>
                                <div className="font-mono">{result.contract_size.toLocaleString()}</div>
                            </div>
                            {result.quote_currency !== 'USD' && (
                                <div>
                                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Risk in {result.quote_currency}</div>
                                    <div className="font-mono">{result.risk_quote_currency.toLocaleString()} {result.quote_currency}</div>
                                </div>
                            )}
                        </div>
                        <div
                            className="flex items-start gap-2.5 rounded-md p-3 text-xs leading-relaxed"
                            style={{ backgroundColor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', color: '#F59E0B' }}
                            data-testid="risk-spread-warning"
                        >
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>{result.warning}</span>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
